import React, { PureComponent } from 'react';
import { Grid, Paper } from '@mui/material';
import { MapReact, layer, control, Controls, Layers, Interactions, source, Overlays } from 'react-openlayers';
import { Units } from 'ol/control/ScaleLine';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import XYZ from 'ol/source/XYZ';
import Map from 'ol/Map';
import MapButton from './buttons/MapButton';

import { MdSatellite } from 'react-icons/md';
import { TbShovel } from 'react-icons/tb';
import { Extent, getCenter } from 'ol/extent';
import MapEvent from 'ol/MapEvent';
import BaseEvent from 'ol/events/Event';
import {
  MapCenterStorage,
  MapExtentStorage,
  MapPixelsPerMeterStorage,
  MapPointResolution,
  MapResolutionStorage,
  MapRotationStorage,
  MapSizeStorage,
  MapSmoothZoomEnabled,
  MapZoomStorage,
  MaxZoom,
} from '../../db/local_storage';
import EventBus from '../../EventBus';
import { Coordinate } from 'ol/coordinate';
import { getPointResolution } from 'ol/proj';
import {
  dispatchCloseSampleNeedsUpdate,
  dispatchMapExtentUpdated,
  dispatchMapPointResolutionUpdated,
  dispatchMapResolutionUpdated,
  dispatchPixelsPerMeterUpdated,
  dispatchZoomUpdated,
  MAP_EVENTS,
} from '../../mapEvents';

interface BaseMapProps {
  absoluteOverlays?: React.ReactNode[];
  trackingButton?: React.ReactNode;
  overlays?: React.ReactNode[];
  layers: React.ReactNode[];
  buttons: React.ReactNode[];
  // Read-only mission status (e.g. "Target: 16", "Actual: 17"). Rendered
  // left-aligned in a thin strip above the action toolbar. Strip is hidden
  // when both statusItems and primaryActions are empty.
  statusItems?: React.ReactNode[];
  // Important text actions that don't belong next to icon toggles
  // (e.g. "Close Mission"). Right-aligned in the same strip.
  primaryActions?: React.ReactNode[];
  secondRowElements?: React.ReactNode[];
  enableSoilMap?: boolean;
  robotConnected?: boolean;
  // Optional external tile source for historical layers
  externalTileSource?: {
    url: string;
    attribution: string;
    sourceType?: 'xyz' | 'wms'; // Specify source type
    wmsParams?: {
      // WMS-specific parameters
      LAYERS: string;
      TIME?: string;
    };
  };
  // Callback when map center changes
  onCenterChange?: (center: Coordinate) => void;
}

interface BaseMapState {
  sourceUrl: string;
  sourceType: 'xyz' | 'wms';
  wmsParams?: { LAYERS: string; TIME?: string };
  sourceKey: string; // Used to force layer re-render when source changes
  tracking: boolean;
  soilMapEnabled: boolean;
  zoom?: { value: number };
  center: { value: Coordinate };
  rotation?: { value: number };
  extent?: { value: Extent };
  customTileLayer?: TileLayer; // Custom tile layer for WMS
  olMap?: Map; // Reference to OpenLayers map
}

export default class BaseMap extends PureComponent<BaseMapProps, BaseMapState> {
  // https://ibasemaps-api.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/17/49254/33805?token=AAPKc4453f45a17f4c65a0f870a821cf18a1rPaK8Q6iK6cEOfKEQY0Y0G4S36ZlzCk8tQYQv9uNkn51GJnSH8BTuf9d6q6y6oSX
  // https://ecn.t2.tiles.virtualearth.net/tiles/a0320001000221211?g=15401&amp;mkt=en-US&amp;key=AmmH5JF22z1SVTlmW5RBL9Qs24jux_Rn2C6MSGoRHohVCySTPQBcsOf4VEqIvtye
  // https://ecn.t2.tiles.virtualearth.net/tiles/a0320001000221220?g=15401&amp;mkt=en-US&amp;key=AmmH5JF22z1SVTlmW5RBL9Qs24jux_Rn2C6MSGoRHohVCySTPQBcsOf4VEqIvtye
  // https://casoilresource.lawr.ucdavis.edu/cgi-bin/mapserv?map=/data1/website/gmap/mapunit_wms.map&layers=ssurgo&layers=ssa&mode=tile&tilemode=gmap&tile=33805+49256+17&label=musym

  SOIL_MAP =
    'https://casoilresource.lawr.ucdavis.edu/cgi-bin/mapserv?map=/data1/website/gmap/mapunit_wms.map&layers=ssurgo&layers=ssa&mode=tile&tilemode=gmap&tile={x}+{y}+{z}';
  GOOGLE_SAT = 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}';
  GOOGLE_ROAD = 'https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}';

  ATTRIBUTION = '@ Google';
  PROJECTION = 'EPSG:3857';
  INITIAL_ROTOATION = 0;
  renderCount = 0;

  constructor(props: BaseMapProps) {
    super(props);

    const initialUrl = props.externalTileSource?.url || this.GOOGLE_SAT;
    const sourceType = props.externalTileSource?.sourceType || 'xyz';
    const wmsParams = props.externalTileSource?.wmsParams;

    // Create unique key including WMS params to ensure proper re-renders
    const sourceKey = wmsParams ? `${initialUrl}-${wmsParams.LAYERS}-${wmsParams.TIME || ''}` : initialUrl;

    this.state = {
      sourceUrl: initialUrl,
      sourceType: sourceType,
      wmsParams: wmsParams,
      sourceKey: sourceKey, // Track source changes
      tracking: false,
      soilMapEnabled: false,
      center: { value: MapCenterStorage.get() },
      rotation: MapRotationStorage.get(),
      extent: { value: MapExtentStorage.get() },
      zoom: MapZoomStorage.get(),
    };

    this.switchSource = this.switchSource.bind(this);
    this.handleMapMove = this.handleMapMove.bind(this);
  }

  /**
   * Switch the map source
   */
  switchSource() {
    if (this.state.sourceUrl === this.GOOGLE_SAT) {
      this.setState({ sourceUrl: this.GOOGLE_ROAD });
    } else {
      this.setState({ sourceUrl: this.GOOGLE_SAT });
    }
  }

  componentDidMount() {
    EventBus.on(MAP_EVENTS.SET_CENTER, this.centerUpdated);
    EventBus.on(MAP_EVENTS.SET_ROTATION, this.rotationUpdated);
    EventBus.on(MAP_EVENTS.SET_ZOOM, this.setZoom);
    EventBus.on(MAP_EVENTS.SET_FIT, this.updateFit);
  }

  componentWillUnmount() {
    EventBus.remove(MAP_EVENTS.SET_CENTER, this.centerUpdated);
    EventBus.remove(MAP_EVENTS.SET_ROTATION, this.rotationUpdated);
    EventBus.remove(MAP_EVENTS.SET_ZOOM, this.setZoom);
    EventBus.remove(MAP_EVENTS.SET_FIT, this.updateFit);
  }

  componentDidUpdate(prevProps: BaseMapProps) {
    // Update tile source if external tile source changes
    if (
      this.props.externalTileSource?.url !== prevProps.externalTileSource?.url ||
      this.props.externalTileSource?.sourceType !== prevProps.externalTileSource?.sourceType ||
      JSON.stringify(this.props.externalTileSource?.wmsParams) !==
        JSON.stringify(prevProps.externalTileSource?.wmsParams)
    ) {
      const newUrl = this.props.externalTileSource?.url || this.GOOGLE_SAT;
      const sourceType = this.props.externalTileSource?.sourceType || 'xyz';
      const wmsParams = this.props.externalTileSource?.wmsParams;

      // Create a unique key that includes WMS params for proper re-rendering
      const sourceKey = wmsParams ? `${newUrl}-${wmsParams.LAYERS}-${wmsParams.TIME || ''}` : newUrl;

      this.setState({
        sourceUrl: newUrl,
        sourceType: sourceType,
        wmsParams: wmsParams,
        sourceKey: sourceKey, // Update key to force re-render
      });
    }
  }

  setZoom = (zoom: number) => {
    if (!MapSmoothZoomEnabled.get()) {
      // instant zoom
      this.setState({ zoom: { value: zoom } });
    } else {
      // staggered zoom
      const currentZoom = this.state.zoom?.value;

      if (currentZoom === undefined) {
        return;
      }

      const diff = zoom - currentZoom;
      const steps = 100;
      const delay_s = 2;
      const delay_ms = delay_s * 1000;
      const ms_per_loop = delay_ms / steps;
      let i = 0;
      const interval = setInterval(() => {
        const newZoom = currentZoom + (diff / steps) * i;
        this.setState({ zoom: { value: newZoom } });
        i++;

        if (i > steps) {
          clearInterval(interval);
        }
      }, ms_per_loop);
    }
  };

  updateFit = (extent: Extent) => {
    this.setState({ extent: { value: extent } });
  };

  centerUpdated = (center: Coordinate) => {
    this.setState({ center: { value: center } });
  };

  rotationUpdated = (rotation: number) => {
    this.setState({ rotation: { value: rotation } });
  };

  /**
   * Handle for map onmoveend that grabs the center and zoom of map view and saves it to localstorage
   * @param {*} event
   */
  handleMapMove(event: MapEvent | BaseEvent) {
    if (!('map' in event)) {
      return;
    }

    const view = event.map.getView();

    if (!view) {
      return;
    }

    const extent = view.calculateExtent();
    const resolution = view.getResolution();

    const StateUpdates: Partial<BaseMapState> = {};

    if (JSON.stringify(extent) !== JSON.stringify(MapExtentStorage.get())) {
      MapExtentStorage.set(extent);
      dispatchMapExtentUpdated(extent);
    }

    let zoomUpdated = false;
    const zoom = view.getZoom();

    if (!!zoom && zoom !== MapZoomStorage.get().value) {
      MapZoomStorage.set({ value: zoom });
      StateUpdates.zoom = { value: zoom };
      dispatchZoomUpdated(zoom);
      zoomUpdated = true;
    }

    const center = view.getCenter();

    if (center) {
      MapCenterStorage.set(center);
      // Notify parent component of center change
      this.props.onCenterChange?.(center);
    }

    MapSizeStorage.set(event.map.getSize());

    if (!resolution) {
      if (zoomUpdated) {
        dispatchCloseSampleNeedsUpdate();
      }

      return;
    }

    if (resolution !== MapResolutionStorage.get()) {
      MapResolutionStorage.set(resolution);
      dispatchMapResolutionUpdated(resolution);
    }

    const resCenter = getPointResolution('EPSG:3857', 1, getCenter(extent));

    if (resCenter !== MapPointResolution.get()) {
      MapPointResolution.set(resCenter);
      dispatchMapPointResolutionUpdated(resCenter);
    }

    const pixelsPerMeter = 1 / resolution / resCenter;

    if (pixelsPerMeter !== MapPixelsPerMeterStorage.get()) {
      MapPixelsPerMeterStorage.set(pixelsPerMeter);
      dispatchPixelsPerMeterUpdated(pixelsPerMeter);
    }

    // @ts-ignore
    this.setState(StateUpdates);

    // Basically when we change zoom or move we have to
    // redraw core circles because they are drawn with a
    // relationship to the pixel size to physical distance ratio of the map.
    // We need to call this below MapPixelsPerMeterStorage.set(pixelsPerMeter);
    // because KMLLayer (and maybe other files) use pixelsPerMeter storage value.
    // If we call dispatchCoresNeedUpdate before setting this value, KMLLayer uses the old
    // pixelsPerMeter value and ends up drawing circles with wrong scale.
    // TODO: refactor - we shouldn't rely on the order of those events firing.
    // Maybe come up with one combined event with all up-to-date values.
    if (zoomUpdated) {
      dispatchCloseSampleNeedsUpdate();
    }
  }

  render() {
    this.renderCount++;

    // console.log(this.renderCount,
    //     'extent', JSON.stringify(this.state.extent),
    //     'zoom', this.state.zoom?.value,
    //     'center', this.state.center.value,
    //     'rotation', this.state.rotation?.value);
    const statusItems = (this.props.statusItems ?? []).filter(Boolean);
    const primaryActions = (this.props.primaryActions ?? []).filter(Boolean);
    const showStatusStrip = statusItems.length > 0 || primaryActions.length > 0;

    return (
      <Grid container direction="row" size={12}>
        {showStatusStrip && (
          <Grid container direction="row" size={12} style={{ maxHeight: 28 }}>
            <Paper
              style={{
                width: '100%',
                height: 28,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0 12px',
                gap: 12,
                // `overflow: auto` + `overscroll-behavior: contain` makes the
                // strip a scroll container that consumes wheel events without
                // chaining them to the page, even though it never actually
                // scrolls (content fits in 28px).
                overflow: 'auto',
                overscrollBehavior: 'contain',
              }}
              elevation={2}
              className={'scroll-show'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, overflow: 'hidden' }}>
                {statusItems}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>{primaryActions}</div>
            </Paper>
          </Grid>
        )}
        <Grid container direction="row" size={12} style={{ maxHeight: 40, overflow: 'hidden' }}>
          <Paper
            style={{
              width: '100%',
              height: 40,
              display: 'flex',
              overflow: 'auto',
              overscrollBehavior: 'contain',
              paddingRight: 15,
            }}
            elevation={5}
            className={'scroll-show'}
          >
            <MapButton
              tooltip={this.state.sourceUrl === this.GOOGLE_SAT ? 'Switch to Road Map' : 'Switch to Satellite Map'}
              key={'SwitchMapSourceButton'}
              onClick={this.switchSource}
            >
              <MdSatellite size={22} />
            </MapButton>
            {this.props.enableSoilMap && (
              <MapButton
                tooltip={'Enable Soil Map'}
                key={'EnableSoilMapButton'}
                toggled={this.state.soilMapEnabled}
                onClick={() => {
                  this.setState({ soilMapEnabled: !this.state.soilMapEnabled });
                }}
              >
                <TbShovel size={22} />
              </MapButton>
            )}
            {this.props.buttons}
          </Paper>
        </Grid>
        {this.props.secondRowElements && (
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            spacing={2}
            direction="row"
            style={{ height: 60, overflow: 'hidden' }}
            size={12}
          >
            {this.props.secondRowElements}
          </Grid>
        )}
        {this.props.absoluteOverlays}
        {this.props.trackingButton}
        <Grid container size={12}>
          {/* @ts-ignore */}
          <MapReact
            style={{
              // Subtract every row stacked above the map so it fits the viewport
              // exactly. Otherwise the map overflows the bottom of the window and
              // bottom-anchored controls (e.g. the ScaleLine at `bottom: 8px`) fall
              // off-screen. The status strip (28px) was previously unaccounted for.
              height: `calc(100vh - ${(this.props.secondRowElements ? 100 : 80) + (showStatusStrip ? 28 : 0)}px)`,
              width: '100%',
            }}
            view={{
              projection: this.PROJECTION,
              maxZoom: MaxZoom.get(),
            }}
            center={this.state.center}
            rotation={this.state.rotation}
            zoom={this.state.zoom}
            fit={this.state.extent}
            onMoveend={this.handleMapMove}
          >
            <Overlays>{this.props.overlays}</Overlays>
            {/* @ts-ignore */}
            <Controls>
              {/*
                            // @ts-ignore */}
              <control.ScaleLineReact units={Units.US} />
              {/* // this was removed because no one was passing in this prop */}
              {/* {this.props.controls} */}
            </Controls>
            <Layers>
              {this.state.sourceType === 'wms' && this.state.wmsParams ? (
                /* WMS Source - Use a custom approach with tile coordinates */
                <layer.TileReact key={this.state.sourceKey} zIndex={-1}>
                  {/* @ts-ignore */}
                  <source.XYZReact
                    url={(() => {
                      // Create a tile URL function that converts XYZ to WMS bbox
                      // OpenLayers will call this with tile coordinates
                      const baseUrl = this.state.sourceUrl;
                      const layers = this.state.wmsParams.LAYERS;
                      const time = this.state.wmsParams.TIME;

                      // Build WMS URL with required parameters
                      // WMS 1.1.1 spec requires: SERVICE, VERSION, REQUEST, LAYERS, STYLES, SRS, BBOX, WIDTH, HEIGHT, FORMAT
                      // Note: Using SRS for WMS 1.1.1 (older servers), CRS is WMS 1.3.0
                      let url = `${baseUrl}?SERVICE=WMS&VERSION=1.1.1&REQUEST=GetMap&LAYERS=${layers}&STYLES=&WIDTH=256&HEIGHT=256&FORMAT=image/png&SRS=EPSG:3857&BBOX={x},{y},{z}`;

                      // Only add TIME parameter if it's actually provided
                      if (time) {
                        url += `&TIME=${time}`;
                      }

                      return url;
                    })()}
                    tileLoadFunction={(tile: any, src: string) => {
                      // Extract tile coordinates from the dummy URL
                      const match = src.match(/BBOX=(\d+),(\d+),(\d+)/);

                      if (match) {
                        const x = parseInt(match[1]);
                        const y = parseInt(match[2]);
                        const z = parseInt(match[3]);

                        // Calculate EPSG:3857 bbox from tile coordinates
                        const tileToMercatorBbox = (x: number, y: number, z: number): string => {
                          const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z);
                          const lon1 = (x / Math.pow(2, z)) * 360 - 180;
                          const lat1 = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));

                          const lon2 = ((x + 1) / Math.pow(2, z)) * 360 - 180;
                          const n2 = Math.PI - (2 * Math.PI * (y + 1)) / Math.pow(2, z);
                          const lat2 = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n2) - Math.exp(-n2)));

                          const merc_x1 = (lon1 * 20037508.34) / 180;
                          const merc_y1 =
                            ((Math.log(Math.tan(((90 + lat1) * Math.PI) / 360)) / (Math.PI / 180)) * 20037508.34) / 180;
                          const merc_x2 = (lon2 * 20037508.34) / 180;
                          const merc_y2 =
                            ((Math.log(Math.tan(((90 + lat2) * Math.PI) / 360)) / (Math.PI / 180)) * 20037508.34) / 180;

                          return `${merc_x1},${merc_y2},${merc_x2},${merc_y1}`;
                        };

                        const bbox = tileToMercatorBbox(x, y, z);
                        const realUrl = src.replace(/BBOX=\d+,\d+,\d+/, `BBOX=${bbox}`);

                        // Load the image with the real WMS URL
                        (tile as any).getImage().src = realUrl;
                      }
                    }}
                    attributions={this.props.externalTileSource?.attribution || this.ATTRIBUTION}
                  />
                </layer.TileReact>
              ) : (
                /* XYZ Source - standard tile source */
                <layer.TileReact key={this.state.sourceKey} zIndex={-1}>
                  {/* @ts-ignore */}
                  <source.XYZReact
                    url={this.state.sourceUrl}
                    attributions={this.props.externalTileSource?.attribution || this.ATTRIBUTION}
                  />
                </layer.TileReact>
              )}
              {this.state.soilMapEnabled && (
                <layer.TileReact zIndex={-1}>
                  <source.XYZReact url={this.SOIL_MAP} attributions={this.ATTRIBUTION} />
                </layer.TileReact>
              )}
              {this.props.layers}
            </Layers>
            {/* @ts-ignore */}
            <Interactions>
              {/* // this was removed because no one was passing in this prop */}
              {/* {this.props.interactions} */}
            </Interactions>
          </MapReact>
        </Grid>
      </Grid>
    );
  }
}
